import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute, PublicOnlyRoute } from "@/components/auth/ProtectedRoute";
import { CrmActionsProvider } from "@/context/CrmActionsContext";
import { ALL_NAV_ITEMS } from "@/config/navigation";

import SignIn from "@/pages/auth/SignIn";
import SignUp from "@/pages/auth/SignUp";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";

import Dashboard from "@/pages/Dashboard";
import ModulePage from "@/pages/ModulePage";
import BrandProfile from "@/pages/BrandProfile";
import CRM from "@/pages/CRM";
import ContactProfile from "@/pages/ContactProfile";
import Pipeline from "@/pages/Pipeline";
import FollowUps from "@/pages/FollowUps";
import Transactions from "@/pages/Transactions";
import TransactionDetail from "@/pages/TransactionDetail";
import Commissions from "@/pages/Commissions";
import Tasks from "@/pages/Tasks";
import Calendar from "@/pages/Calendar";
import Documents from "@/pages/Documents";
import Compliance from "@/pages/Compliance";
import Goals from "@/pages/Goals";
import Analytics from "@/pages/Analytics";
import MarketingCenter from "@/pages/MarketingCenter";
import ContentStudio from "@/pages/ContentStudio";
import ContentCalendar from "@/pages/ContentCalendar";
import Notifications from "@/pages/Notifications";
import {
  AdminCommandCenter, AdminCustomers, AdminFounding, AdminSubscriptions, AdminAnalytics,
  AdminAIOps, AdminFlags, AdminSupport, AdminFeatureRequests, AdminFeedback, AdminAnnouncements, AdminHealth, AdminAudit,
  AdminSecurity, AdminSettings,
} from "@/pages/AdminPages";
import Settings from "@/pages/Settings";
import HelpSupport from "@/pages/HelpSupport";
import Onboarding from "@/pages/Onboarding";
import FoundationShowcase from "@/pages/FoundationShowcase";
import AdminFoundation from "@/pages/AdminFoundation";
import NotFound from "@/pages/NotFound";
import Home from "@/pages/marketing/Home";
import DemoTour from "@/pages/marketing/DemoTour";
import {
  PlatformPage, FeaturesPage, ResaPage, PricingPage, HowItWorksPage,
  ResourcesPage, AboutPage, FaqPage, PrivacyPage, TermsPage,
  CancellationPolicyPage, RefundPolicyPage, ContactPage,
} from "@/pages/marketing/MarketingPages";

// Real pages replace their placeholders.
const REAL_ROUTES = new Set(["/", "/brand-profile", "/settings", "/help", "/welcome", "/crm", "/pipeline", "/follow-ups", "/transactions", "/commissions", "/tasks", "/calendar", "/documents", "/compliance", "/education", "/memberships", "/goals", "/analytics", "/marketing", "/content-calendar"]);

// Build protected placeholder routes for every approved nav item without a real page.
const MODULE_ROUTES = ALL_NAV_ITEMS.filter((i) => !REAL_ROUTES.has(i.path)).map((i) => ({
  path: i.path,
  element: <ModulePage title={i.label} section={i.section} icon={i.icon} />,
}));

// Root: public marketing homepage for visitors; the app Dashboard for authenticated users.
function RootRoute() {
  const { status, isAdmin } = useAuth();
  if (status === "loading") return null;
  if (status === "authenticated") {
    // Workspace-less platform staff have no workspace dashboard; route them to the Admin Command Center.
    if (isAdmin) return <Navigate to="/admin" replace />;
    return <ProtectedRoute><Dashboard /></ProtectedRoute>;
  }
  return <Home />;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <CrmActionsProvider>
              <Routes>
              {/* Public auth routes */}
              <Route path="/login" element={<PublicOnlyRoute><SignIn /></PublicOnlyRoute>} />
              <Route path="/signup" element={<PublicOnlyRoute><SignUp /></PublicOnlyRoute>} />
              <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />

              {/* Public marketing site (Residennce.com) */}
              <Route path="/" element={<RootRoute />} />
              <Route path="/platform" element={<PlatformPage />} />
              <Route path="/demo" element={<DemoTour />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/resa" element={<ResaPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/contact" element={<ContactPage />} />

              {/* Protected app shell */}
              <Route path="/brand-profile" element={<ProtectedRoute requiredPermission="brand_profile.view"><BrandProfile /></ProtectedRoute>} />
              <Route path="/crm" element={<ProtectedRoute requiredPermission="contacts.view"><CRM /></ProtectedRoute>} />
              <Route path="/crm/:id" element={<ProtectedRoute requiredPermission="contacts.view"><ContactProfile /></ProtectedRoute>} />
              <Route path="/pipeline" element={<ProtectedRoute requiredPermission="leads.view"><Pipeline /></ProtectedRoute>} />
              <Route path="/follow-ups" element={<ProtectedRoute requiredPermission="followups.view"><FollowUps /></ProtectedRoute>} />
              <Route path="/transactions" element={<ProtectedRoute requiredPermission="transactions.view"><Transactions /></ProtectedRoute>} />
              <Route path="/transactions/:id" element={<ProtectedRoute requiredPermission="transactions.view"><TransactionDetail /></ProtectedRoute>} />
              <Route path="/commissions" element={<ProtectedRoute requiredPermission="commissions.view"><Commissions /></ProtectedRoute>} />
              <Route path="/tasks" element={<ProtectedRoute requiredPermission="tasks.view"><Tasks /></ProtectedRoute>} />
              <Route path="/calendar" element={<ProtectedRoute requiredPermission="calendar.view"><Calendar /></ProtectedRoute>} />
              <Route path="/documents" element={<ProtectedRoute requiredPermission="documents.view"><Documents /></ProtectedRoute>} />
              <Route path="/compliance" element={<ProtectedRoute requiredPermission="compliance.view"><Compliance /></ProtectedRoute>} />
              <Route path="/education" element={<ProtectedRoute requiredPermission="education.view"><Compliance /></ProtectedRoute>} />
              <Route path="/memberships" element={<ProtectedRoute requiredPermission="memberships.view"><Compliance /></ProtectedRoute>} />
              <Route path="/goals" element={<ProtectedRoute requiredPermission="goals.view"><Goals /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute requiredPermission="analytics.view"><Analytics /></ProtectedRoute>} />
              <Route path="/marketing" element={<ProtectedRoute requiredPermission="marketing.view"><MarketingCenter /></ProtectedRoute>} />
              <Route path="/marketing/studio" element={<ProtectedRoute requiredPermission="marketing.create"><ContentStudio /></ProtectedRoute>} />
              <Route path="/marketing/studio/:id" element={<ProtectedRoute requiredPermission="marketing.edit"><ContentStudio /></ProtectedRoute>} />
              <Route path="/content-calendar" element={<ProtectedRoute requiredPermission="marketing.view"><ContentCalendar /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute requiredPermission="notifications.view"><Notifications /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/help" element={<ProtectedRoute requiredPermission="support.view"><HelpSupport /></ProtectedRoute>} />
              <Route path="/welcome" element={<ProtectedRoute requiredPermission="onboarding.view"><Onboarding /></ProtectedRoute>} />
              {MODULE_ROUTES.map((r) => (
                <Route key={r.path} path={r.path} element={<ProtectedRoute>{r.element}</ProtectedRoute>} />
              ))}
              <Route path="/foundation" element={<ProtectedRoute><FoundationShowcase /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute requiredPermission="admin.access"><AdminCommandCenter /></ProtectedRoute>} />
              <Route path="/admin/customers" element={<ProtectedRoute requiredPermission="admin.customers.view"><AdminCustomers /></ProtectedRoute>} />
              <Route path="/admin/founding" element={<ProtectedRoute requiredPermission="admin.founding.view"><AdminFounding /></ProtectedRoute>} />
              <Route path="/admin/subscriptions" element={<ProtectedRoute requiredPermission="admin.subscriptions.view"><AdminSubscriptions /></ProtectedRoute>} />
              <Route path="/admin/analytics" element={<ProtectedRoute requiredPermission="admin.analytics.view"><AdminAnalytics /></ProtectedRoute>} />
              <Route path="/admin/ai" element={<ProtectedRoute requiredPermission="admin.ai_ops.view"><AdminAIOps /></ProtectedRoute>} />
              <Route path="/admin/flags" element={<ProtectedRoute requiredPermission="admin.access"><AdminFlags /></ProtectedRoute>} />
              <Route path="/admin/support" element={<ProtectedRoute requiredPermission="admin.support.view"><AdminSupport /></ProtectedRoute>} />
              <Route path="/admin/feature-requests" element={<ProtectedRoute requiredPermission="admin.feedback.view"><AdminFeatureRequests /></ProtectedRoute>} />
              <Route path="/admin/feedback" element={<ProtectedRoute requiredPermission="admin.feedback.view"><AdminFeedback /></ProtectedRoute>} />
              <Route path="/admin/announcements" element={<ProtectedRoute requiredPermission="admin.announcements.manage"><AdminAnnouncements /></ProtectedRoute>} />
              <Route path="/admin/health" element={<ProtectedRoute requiredPermission="admin.health.view"><AdminHealth /></ProtectedRoute>} />
              <Route path="/admin/audit" element={<ProtectedRoute requiredPermission="admin.audit.view"><AdminAudit /></ProtectedRoute>} />
              <Route path="/admin/security" element={<ProtectedRoute requiredPermission="admin.security.view"><AdminSecurity /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute requiredPermission="admin.access"><AdminSettings /></ProtectedRoute>} />

              <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
              </Routes>
            </CrmActionsProvider>
            <Toaster position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
